import { Box, styled } from '@mui/material';
import Loader from 'Components/Forms/Loader';
import QuestionnaireTitle from 'FeatureModules/Questionnaire/Components/QuestionnaireTable/QuestionnaireTitle';
import { CardContainer, ThemeTypography } from 'DesignSystem';
import Icon from 'DesignSystem/Primitives/Icon/Icon';
import useQuestionnaireAnswerForm from 'FeatureModules/Questionnaire/hooks/useQuestionnaireAnswerForm';
import React, { FC, useRef } from 'react';
import Moment from 'react-moment';
import { EPartnerRequestStatus } from 'FeatureModules/Partner/types/partner.types';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';
import PageContainer from 'Components/PageContainer';
import PageTitle from 'Components/PageTitle';
import QuestionnaireForm from '../Components/QuestionnaireForm';

const FormContainer = styled(FlexBox)(({ theme }) => ({
  marginTop: theme.spacing(3),
  width: '100%',
  padding: '24px',
  flexDirection: 'column',
  gap: '32px',
  borderRadius: '6px',
  border: `1px solid ${theme.custom.themeColors.grayScale[20]}`,
  background: theme.custom.themeColors.white,
}));

const SavedContainer = styled(FlexBox)(({ theme }) => ({
  padding: '6px 12px',
  gap: '10px',
  borderRadius: '6px',
  background: theme.custom.themeColors.grayScale[10],
}));

const QuestionnaireAnswerForm: FC = () => {
  const {
    questionnaire,
    questionnaireResponse,
    partnerRequest,
    error,
    loading,
    submitting,
    saving,
    handleSave,
    handleFinish,
  } = useQuestionnaireAnswerForm();
  const actionButtonsContainer = useRef<HTMLElement>(null);

  if (loading) {
    return <Loader />;
  }
  if (error || !questionnaireResponse || !questionnaire || !partnerRequest) {
    return <ErrorState />;
  }

  return (
    <PageContainer>
      <PageTitle
        title="Questionnaire overview"
        goBackLabel="All questionnaires"
        titleExtension="Self-assessment"
        goBackUrl="/questionnaires"
      />
      <Box mt={3} />

      <CardContainer padding={2} hasShadow={false}>
        <FlexBox justifyContent="space-between">
          <QuestionnaireTitle
            title={questionnaire.title}
            externalId={questionnaire.externalId}
            year={questionnaire.year}
          />

          <FlexBox gap={1} ref={actionButtonsContainer}>
            {questionnaireResponse.modifiedTimestamp && (
              <SavedContainer>
                <Icon name="check" color="gray-30" size="small" />
                <ThemeTypography variant="BODY_SMALL_BOLD" color="GRAY_80" autoOverflow>
                  {partnerRequest.requestStatus === EPartnerRequestStatus.REQUESTED
                    ? 'Last saved'
                    : partnerRequest.requestStatus === EPartnerRequestStatus.DENIED
                    ? 'Dismissed'
                    : 'Submitted'}{' '}
                  <Moment
                    calendar={{
                      sameDay: '[today] HH:mm:ss',
                      lastDay: '[yesterday] HH:mm',
                      lastWeek: 'YYYY-MM-DD HH:mm',
                      sameElse: 'YYYY-MM-DD HH:mm',
                    }}
                  >
                    {questionnaireResponse.modifiedTimestamp}
                  </Moment>
                </ThemeTypography>
              </SavedContainer>
            )}

            {/* Here the other action buttons will be rendered by the use of createPortal inside the form */}
          </FlexBox>
        </FlexBox>
      </CardContainer>

      <FormContainer>
        <QuestionnaireForm
          response={questionnaireResponse}
          submitting={submitting}
          saving={saving}
          actionButtonsContainer={actionButtonsContainer}
          onSaveClick={handleSave}
          onFinishClick={handleFinish}
        />
      </FormContainer>
    </PageContainer>
  );
};

export default QuestionnaireAnswerForm;
